import { Viewer } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';
import { EquirectangularVideoAdapter } from '@photo-sphere-viewer/equirectangular-video-adapter';
import { VideoPlugin } from '@photo-sphere-viewer/video-plugin';
import '@photo-sphere-viewer/video-plugin/index.css';
import React from 'react';

interface IPanorama360VideoPlayerProps {
    src: string;
    videoId: string;
    height?: string;
    onVideoElement?: (video: HTMLVideoElement) => void;
    // Whether PSV shows its large centred play/pause overlay button. Defaults to
    // true; the customer player sets this to false to keep the view unobstructed.
    showPlayOverlay?: boolean;
    // Whether a plain click on the sphere toggles play/pause. Defaults to false.
    // Dragging to pan/tilt keeps working: PSV only emits `click` when the pointer
    // stayed within its move threshold, so a drag never toggles playback.
    togglePlayOnClick?: boolean;
}

// Thin wrapper around Photo Sphere Viewer (PSV) v5 with its
// EquirectangularVideoAdapter and VideoPlugin. PSV handles sphere rendering,
// mouse/touch drag, zoom, fullscreen, gyroscope, and the play / scrub /
// volume controls (the VideoPlugin's progressbar plus the `video` navbar item).
//
// `withCredentials: true` instructs the adapter to set
// `crossOrigin = "use-credentials"` on the underlying <video>, matching the
// auth flow of the rest of the application (cookies on the
// `/api/assets/:id/download` 302 redirect to S3 / CDN).
//
// IMPORTANT: PSV's video adapter uses the <video> element purely as a WebGL
// texture source and NEVER appends it to the DOM. That means it cannot be found
// via `container.querySelector('video')` or `document.querySelector('#id')`.
// Instead we read it from the public `panorama-loaded` event, whose
// `data.texture.image` IS the underlying <video> element, and forward it via
// `onVideoElement`. This lets SurveyVideoDetails keep its existing
// snap / fragment / slider / slowmotion / frame-by-frame logic, all of which
// work directly on the DOM video element (currentTime, playbackRate,
// addEventListener), and PSV keeps driving playback through the same element.
export class Panorama360VideoPlayer extends React.Component<IPanorama360VideoPlayerProps> {
    private readonly containerRef = React.createRef<HTMLDivElement>();
    private viewer: Viewer | undefined;
    private hasForwardedVideo = false;

    public override componentDidMount() {
        const container = this.containerRef.current;
        if (!container) {
            return;
        }

        this.viewer = new Viewer({
            container,
            adapter: EquirectangularVideoAdapter,
            // `bigbutton` controls PSV's large centred play overlay; the customer
            // player hides it (showPlayOverlay === false) for an unobstructed view.
            plugins: [[VideoPlugin, { bigbutton: this.props.showPlayOverlay !== false }]],
            panorama: { source: this.props.src },
            withCredentials: true,
            navbar: 'video volume caption fullscreen',
        });

        // Registered before the panorama finishes loading (loading needs network
        // I/O for the video metadata), so the event is never missed.
        this.viewer.addEventListener('panorama-loaded', (e) => this.forwardVideoElement(e.data.texture));

        if (this.props.togglePlayOnClick) {
            // PSV fires `click` from __stopMove() only when the move threshold was
            // never crossed, so a click-and-drag pans the view without toggling
            // playback. Right clicks (context menu) are ignored.
            this.viewer.addEventListener('click', (e) => {
                if (e.data.rightclick) {
                    return;
                }
                this.viewer?.getPlugin<VideoPlugin>(VideoPlugin).playPause();
            });
        }
    }

    public override componentWillUnmount() {
        this.viewer?.destroy();
        this.viewer = undefined;
    }

    public override render() {
        const height = this.props.height ?? '500px';
        return <div ref={this.containerRef} style={{ width: '100%', height }} />;
    }

    // `texture` is PSV's TextureData.texture; for the video adapter it is a
    // three.js VideoTexture whose `image` is the HTMLVideoElement.
    private forwardVideoElement(texture: unknown) {
        if (this.hasForwardedVideo) {
            return;
        }
        const videoEl = (texture as { image?: HTMLVideoElement } | null | undefined)?.image;
        if (!videoEl) {
            return;
        }
        this.hasForwardedVideo = true;
        // Re-assign the id so any existing DOM-id based lookups keep resolving,
        // even though the element itself is not attached to the document.
        videoEl.id = this.props.videoId;
        if (this.props.onVideoElement) {
            this.props.onVideoElement(videoEl);
        }
    }
}
